import React, { useEffect, useState } from "react";
import { Loader2, Percent } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const RATES = [0, 5, 10];

export default function VatBulkDialog({ open, onClose, onSaved }) {
  const [customers, setCustomers] = useState([]);
  const [rates, setRates] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) api.get("/customers").then((r) => { setCustomers(r.data); setRates(Object.fromEntries(r.data.map((c) => [c.id, c.vat_rate || 0]))); }); }, [open]);

  const setAll = (r) => setRates(Object.fromEntries(customers.map((c) => [c.id, r])));
  const save = async () => {
    setSaving(true);
    try {
      const groups = RATES.map((r) => ({ r, ids: customers.filter((c) => rates[c.id] === r && (c.vat_rate || 0) !== r).map((c) => c.id) })).filter((g) => g.ids.length);
      const res = await Promise.all(groups.map((g) => api.put("/customers/vat-bulk", { customer_ids: g.ids, vat_rate: g.r })));
      toast.success(`${res.reduce((s, x) => s + x.data.updated, 0)} müşteri güncellendi`); onSaved?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const counts = RATES.map((r) => customers.filter((c) => rates[c.id] === r).length);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="vat-bulk-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Percent className="h-5 w-5 text-primary" /> Toplu KDV Ayarı</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Her müşteri için satış fiyatına eklenecek KDV oranını seçin. Değişiklik sonraki girişlerden itibaren geçerlidir.</p>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">Tümünü:</span>
          {RATES.map((r) => <button key={r} onClick={() => setAll(r)} data-testid={`vat-bulk-all-${r}`} className="rounded-full border border-border px-2.5 py-1 hover:bg-accent">%{r}</button>)}
          <span className="ml-auto text-muted-foreground" data-testid="vat-bulk-count">%0: {counts[0]} · %5: {counts[1]} · %10: {counts[2]}</span>
        </div>
        <div className="max-h-80 overflow-y-auto rounded-xl border border-border divide-y divide-border/60" data-testid="vat-bulk-list">
          {customers.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Müşteri yok</div>}
          {customers.map((c) => { const r = rates[c.id] || 0; return (
            <div key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`vat-bulk-row-${c.id}`}>
              <div className="flex-1 min-w-0"><div className="font-medium truncate">{c.short_name || c.name}</div><div className="font-mono text-xs text-muted-foreground">{TRY(c.unit_price)}{r > 0 && <span className="text-primary"> → {TRY(c.unit_price * (1 + r / 100))}</span>}</div></div>
              <div className="inline-flex rounded-lg bg-muted/60 p-0.5">
                {RATES.map((x) => (
                  <button key={x} onClick={() => setRates({ ...rates, [c.id]: x })} data-testid={`vat-bulk-rate-${x}-${c.id}`}
                    className={`h-7 px-2.5 rounded-md text-xs font-semibold ${r === x ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>%{x}</button>
                ))}
              </div>
            </div>
          ); })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving || customers.length === 0} data-testid="vat-bulk-save" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Uygula</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
